/**
 * SignInModal — marketing-nav chooser.
 *
 * Restored from git (deleted commit b33e7d3) with three updates:
 *   1. Routes employee path to /apply directly (the old /signin redirect
 *      is still in place but one-hop routing is cleaner).
 *   2. i18n namespace moved to `nav.signinModal.*` per product spec.
 *   3. Strengthened a11y — focus-first on open, focus-restore on close,
 *      Tab key trap inside the modal.
 *
 * Reuses the `.modal-overlay / .modal-content / .modal-choice` CSS that
 * survived the previous deletion (`index.css` 1678-1770).
 */
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import I18nText from "./I18nText";
import { track } from "../lib/analytics";

export default function SignInModal({ open, onClose }) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const overlayRef = useRef(null);
    const firstChoiceRef = useRef(null);
    const lastChoiceRef = useRef(null);
    const triggerRef = useRef(null);

    // Capture the element that had focus when the modal opened, so we
    // can restore focus to it on close (fallback: document.body).
    useEffect(() => {
        if (!open) return;
        triggerRef.current = document.activeElement;
        track("sign_in_modal_open", {});
        // Focus first choice on next tick so the browser has painted.
        const timer = setTimeout(() => {
            if (firstChoiceRef.current) firstChoiceRef.current.focus();
        }, 0);
        return () => clearTimeout(timer);
    }, [open]);

    // ESC closes; Tab cycles focus between the two choices (minimal trap
    // — we only have two focusable elements inside the content, plus the
    // close button).
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose();
            } else if (e.key === "Tab") {
                // Trap within the modal content. The close (×) button is
                // the only other focusable beyond the two choices, but
                // browsers handle it natively — here we only need to
                // stop the focus from escaping out of the overlay.
                const focusables =
                    overlayRef.current &&
                    overlayRef.current.querySelectorAll(
                        'button, [href], input, [tabindex]:not([tabindex="-1"])',
                    );
                if (!focusables || focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // On close, restore focus to the trigger that opened us.
    useEffect(() => {
        if (open) return;
        if (triggerRef.current && typeof triggerRef.current.focus === "function") {
            triggerRef.current.focus();
            triggerRef.current = null;
        }
    }, [open]);

    if (!open) return null;

    const pick = (portal) => {
        track(
            portal === "employee"
                ? "sign_in_modal_employee"
                : "sign_in_modal_employer",
            {},
        );
        onClose();
        navigate(portal === "employee" ? "/apply" : "/employer-signin");
    };

    return (
        <div
            ref={overlayRef}
            className="modal-overlay"
            data-testid="signin-modal-overlay"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="modal-content"
                data-testid="signin-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="signin-modal-title"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className="modal-close"
                    onClick={onClose}
                    aria-label={t("nav.signinModal.closeAria")}
                    data-testid="signin-modal-close"
                    type="button"
                >
                    ×
                </button>
                <div className="section-label mono" style={{ marginBottom: "1.5rem" }}>
                    {t("nav.signinModal.label")}
                </div>
                <h3 className="modal-title" id="signin-modal-title">
                    <I18nText i18nKey="nav.signinModal.title" />
                </h3>
                <div className="modal-choices">
                    <button
                        ref={firstChoiceRef}
                        type="button"
                        className="modal-choice"
                        data-testid="signin-choice-employee"
                        onClick={() => pick("employee")}
                    >
                        <div className="modal-choice-label mono">
                            {t("nav.signinModal.employeeLabel")}
                        </div>
                        <div className="modal-choice-title">
                            {t("nav.signinModal.employeeTitle")}
                        </div>
                        <div className="modal-choice-sub">
                            {t("nav.signinModal.employeeSub")}
                        </div>
                        <div className="modal-choice-cta">
                            {t("nav.signinModal.employeeCta")} →
                        </div>
                    </button>
                    <button
                        ref={lastChoiceRef}
                        type="button"
                        className="modal-choice"
                        data-testid="signin-choice-employer"
                        onClick={() => pick("employer")}
                    >
                        <div className="modal-choice-label mono">
                            {t("nav.signinModal.employerLabel")}
                        </div>
                        <div className="modal-choice-title">
                            {t("nav.signinModal.employerTitle")}
                        </div>
                        <div className="modal-choice-sub">
                            {t("nav.signinModal.employerSub")}
                        </div>
                        <div className="modal-choice-cta">
                            {t("nav.signinModal.employerCta")} →
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
}
